import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../api";
import { FaPlug, FaCheckCircle, FaRegCircle } from "react-icons/fa";

// Champs par provider. Les secrets ne sont jamais renvoyés par l'API → champs
// vides à l'affichage ; on n'envoie que ce qui est saisi.
const PROVIDERS = [
  {
    id: "qonto",
    label: "Qonto (facturation électronique)",
    fields: [
      { key: "slug", label: "Organization slug", type: "text" },
      { key: "secret", label: "Secret key", type: "password" },
      { key: "stagingToken", label: "Staging token (sandbox, optionnel)", type: "password" },
      { key: "baseUrl", label: "Base URL (optionnel)", type: "text" },
    ],
  },
  {
    id: "dbep",
    label: "Des Bras en Plus (import missions)",
    fields: [
      { key: "user", label: "Utilisateur", type: "text" },
      { key: "pass", label: "Mot de passe", type: "password" },
      { key: "baseUrl", label: "Base URL (optionnel)", type: "text" },
    ],
  },
  {
    id: "nextories",
    label: "Nextories (import leads)",
    fields: [
      { key: "user", label: "Utilisateur", type: "text" },
      { key: "pass", label: "Mot de passe", type: "password" },
      { key: "baseUrl", label: "Base URL (optionnel)", type: "text" },
    ],
  },
];

function ProviderCard({ meta, status, onSaved }) {
  const [form, setForm] = useState({});
  const [enabled, setEnabled] = useState(status?.enabled ?? true);
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const config = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v !== undefined && v !== "")
    );
    if (Object.keys(config).length === 0 && status?.configured) {
      // Aucun champ modifié : on ne pousse que l'état enabled.
    }
    setSaving(true);
    try {
      await api.put(`/integrations/${meta.id}`, { config, enabled });
      toast.success(`${meta.label} enregistré`);
      setForm({});
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer la configuration ${meta.label} ?`)) return;
    try {
      await api.delete(`/integrations/${meta.id}`);
      toast.success("Configuration supprimée");
      onSaved?.();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="rounded-xl border border-base-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FaPlug className="text-primary" />
          <span className="font-semibold">{meta.label}</span>
        </div>
        <span className={`flex items-center gap-1.5 text-xs font-medium ${status?.configured ? "text-success" : "text-base-content/40"}`}>
          {status?.configured ? <FaCheckCircle /> : <FaRegCircle />}
          {status?.configured ? "Configuré" : "Non configuré"}
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {meta.fields.map((f) => (
          <label key={f.key} className="text-sm">
            <span className="block text-base-content/60 mb-1">{f.label}</span>
            <input
              type={f.type}
              value={form[f.key] ?? ""}
              autoComplete="new-password"
              placeholder={status?.configured ? "•••••• (inchangé)" : ""}
              onChange={(e) => setField(f.key, e.target.value)}
              className="input input-bordered input-sm w-full"
            />
          </label>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="toggle toggle-sm toggle-primary" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Activé
        </label>
        <div className="flex gap-2">
          {status?.configured && (
            <button onClick={remove} className="btn btn-ghost btn-sm text-error">Supprimer</button>
          )}
          <button onClick={save} disabled={saving} className="btn btn-primary btn-sm">
            {saving ? "…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IntegrationsTab() {
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/integrations");
      setStatuses(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const byId = Object.fromEntries(statuses.map((s) => [s.provider, s]));

  if (loading) return <div className="loading loading-spinner text-primary" />;

  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-base-content/50">
        {t("settings.integrations.hint", "Identifiants propres à votre organisation. Stockés chiffrés ; jamais réaffichés.")}
      </p>
      {PROVIDERS.map((meta) => (
        <ProviderCard key={meta.id} meta={meta} status={byId[meta.id]} onSaved={fetchStatus} />
      ))}
    </div>
  );
}
